import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { BrandingProvider } from './context/BrandingContext';
import { AuthProvider } from './context/AuthContext';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ModernLayout } from './components/layout/ModernLayout';
import { Login } from './pages/Login';
import { ModernDashboard } from './pages/ModernDashboard';
import { Agents } from './pages/Agents';
import AgentConfig from './pages/AgentConfig';
import { Chatbots } from './pages/Chatbots';
import { CreateChatbot } from './pages/CreateChatbot';
// import { Analytics } from './pages/Analytics';
import ChatbotConfig from './pages/ChatbotConfig';
import { Workflows } from './pages/Workflows';
import WorkflowEditor from './pages/WorkflowEditor';
import { Reports } from './pages/Reports';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { Profile } from './pages/Profile';
import { NotFound } from './pages/NotFound';
import { AgentTypes } from './pages/AgentTypes';
import PublicChatWidget from './pages/PublicChatWidget';
import McpOauthCallback from './pages/McpOauthCallback';
import './index.css';
import { ToastContainer } from 'react-toastify';
import Signup from './pages/Signup';
import SessionHandoff from './pages/SessionHandoff';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import 'react-image-crop/dist/ReactCrop.css';
import TenantListing from './pages/SuperAdmin/TenantListing';
import AgentType from './pages/SuperAdmin/AgentType';
import AiPod from './pages/SuperAdmin/AiPod';
import AiPodConfigure from './pages/SuperAdmin/AiPod/Configure';
import AiPodPlans from './pages/SuperAdmin/AiPod/Plans';
import AiPodChatConfigure from './pages/SuperAdmin/AiPod/ChatConfigure';
import CategoryPage from './pages/SuperAdmin/Category';
import IndustryPage from './pages/SuperAdmin/Industry';
import TenantConfig from './pages/SuperAdmin/TenantConfig';
import McpCatalogPage from './pages/SuperAdmin/McpCatalog';
import FeatureRegistryPage from './pages/SuperAdmin/Features';
import FeatureDetailPage from './pages/SuperAdmin/Features/Detail';
import PodDetail from './pages/PodDetail';
import PodPlans from './pages/PodPlans';
import PodCheckout from './pages/PodCheckout';
import PodManage from './pages/PodManage';
import { ReRouting } from './pages/ReRouting';
import Departments from './pages/Departments';
import UsersList from './pages/UsersList';
import PublicEcosystemChatWidget from './pages/PublicEcosystemChatWidget';
import Ecosystems from './pages/Ecosystems';
import EcosystemConfig from './pages/EcosystemConfig';


/**
 * Main App component that sets up routing and global providers
 * 
 * @returns {JSX.Element} The main application component
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ToastContainer position="top-right" />
      <Router>
        <AuthProvider>
        {/* Inside AuthProvider: branding follows the signed-in user's client. */}
        <BrandingProvider>
          <AppProvider>
            <Routes>
                {/* Public Routes */}

                {/*
                  Lands a session moved from another hostname. Deliberately outside
                  ProtectedRoute: the arriving user has no session on this origin
                  yet — the ticket in the URL fragment is what establishes one.
                */}
                <Route path="/session/handoff" element={<SessionHandoff />} />
                <Route 
                  path="/login" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Login />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/signup" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Signup />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/forgot-password" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ForgotPassword />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/reset-password/verify" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ResetPassword />
                    </ProtectedRoute>
                  } 
                />

                {/* Public Chat Widget Route - No Auth Required */}
                <Route
                  path="/chat/:chatbotId/:clientId"
                  element={<PublicChatWidget />}
                />

                {/* Public Ecosystem Chat Widget Route - No Auth Required */}
                <Route
                  path="/eco-chat/:ecosystemId/:clientId"
                  element={<PublicEcosystemChatWidget />}
                />

                <Route
                  path="/oauth/mcp/callback"
                  element={<McpOauthCallback />}
                />

                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <ModernLayout>
                        <Routes>
                          <Route path="/" element={<ReRouting />} />
                          <Route path="/dashboard" element={<ModernDashboard />} />
                          <Route path="/agents" element={<Agents />} />
                          <Route path="/agents/:agentId/config" element={<AgentConfig />} />
                          <Route path="/agent-types" element={<AgentTypes />} />
                          <Route path="/pods/:podId" element={<PodDetail />} />
                          <Route path="/pods/:podId/plans" element={<PodPlans />} />
                          <Route path="/pods/:podId/checkout" element={<PodCheckout />} />
                          <Route path="/pods/:podId/manage" element={<PodManage />} />
                          <Route path="/chatbots" element={<Chatbots />} />
                          <Route path="/chatbots/create" element={<CreateChatbot />} />
                          <Route path="/chatbots/:chatbotId/config" element={<ChatbotConfig />} />
                          {/* Hidden until Analytics has real content; see TopNavigation */}
                          {/* <Route path="/analytics" element={<Analytics />} /> */}
                          <Route path="/workflows" element={<Workflows />} />
                          <Route path="/workflows/new" element={<WorkflowEditor />} />
                          <Route path="/workflows/:workflowId/edit" element={<WorkflowEditor />} />
                          <Route path="/reports" element={<Reports />} />
                          {/* <Route path="/users" element={<Users />} /> */}
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/tenant-listing" element={<TenantListing />} />
                          <Route path="/super-agent-type" element={<AgentType />} />
                          <Route path="/super-ai-pod" element={<AiPod />} />
                          <Route path="/super-ai-pod/:aiPodId/configure" element={<AiPodConfigure />} />
                          <Route path="/super-ai-pod/:aiPodId/chat" element={<AiPodChatConfigure />} />
                          <Route path="/super-ai-pod-plans" element={<AiPodPlans />} />
                          <Route path="/super-mcp-catalog" element={<McpCatalogPage />} />
                          <Route path="/super-features" element={<FeatureRegistryPage />} />
                          <Route path="/super-features/:id" element={<FeatureDetailPage />} />
                          <Route path="/super-categories" element={<CategoryPage />} />
                          <Route path="/super-industries" element={<IndustryPage />} />
                          <Route path="/tenant/:tenantId/config" element={<TenantConfig />} />
                          <Route path="/departments" element={<Departments />} />
                          <Route path="/users" element={<UsersList />} />
                          <Route path="/ecosystems" element={<Ecosystems />} />
                          <Route path="/ecosystems/:ecosystemId/config" element={<EcosystemConfig />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </ModernLayout>
                    </ProtectedRoute>
                  }
                />
            </Routes>
          </AppProvider>
        </BrandingProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
